import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { IconButton, MenuItem, Menu, SxProps, Theme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { SlMenu, useStacklineTheme } from '@stackline/ui';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { useQuery, QueryFunctionContext } from '@tanstack/react-query';
import LegendWithMetric, { LegendWithMetricProps } from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import {
  ALL_LEGEND_SERIES_VISIBLE,
  ChartLegendSeriesKey,
  ChartLegendVisibility,
  ChartLegendVisibilityContext,
  ToggleableLegend,
  useLegendVisibilityState
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/ChartLegendVisibilityContext';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useCsvExporter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useCsvExporter';
import { useQueryKey } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useQueryKey';
import { useAskAdvisorAction } from 'src/components/AtlasV2Components/Common/useAskAdvisorAction';
import { AskAdvisorAIAnimation } from 'src/components/Animations/AskAdvisorAIAnimation/AskAdvisorAIAnimation';
import { useChartContextGroup, useChartContextTitle } from 'src/utils/chartContext/ChartContextGroupContext';
import { getChartContextGroup } from 'src/utils/chartContext/chartContextRegistry';
import { captureElementAsWebp } from 'src/utils/chartContext/captureScreenshot';

const BeaconProChartContext = createContext<{
  chartName: string;
  chartContainerRef: React.RefObject<HTMLDivElement> | null;
  data: any;
  isLoading: boolean;
  /** Visibility of the primary/comparison series, driven by clicking the legend */
  legendVisibility: ChartLegendVisibility;
  toggleLegend: (target: ChartLegendSeriesKey) => void;
  setHoveredLegend: (target: ChartLegendSeriesKey | null) => void;
}>({
  chartName: '',
  chartContainerRef: null,
  data: null,
  isLoading: true,
  legendVisibility: ALL_LEGEND_SERIES_VISIBLE,
  toggleLegend: () => {},
  setHoveredLegend: () => {}
});

const FIVE_MINUTES = 1000 * 60 * 5;

/**
 * BeaconProChart is a compound component used to render visualizations with arbitrary data fetching and rendering logic.
 * The main goal is to provide components that share some state and logic, such as a dropdown to change the data displayed, that can be composed together.
 */
export default function BeaconProChart({
  name,
  children,
  queryFn,
  loadingCallback,
  forceLoading,
  enabled = true
}: {
  name: string;
  children: React.ReactNode;
  queryFn: (context?: QueryFunctionContext) => Promise<unknown>;
  loadingCallback?: (isLoading: boolean) => void;
  forceLoading?: boolean;
  pageSize?: number;
  /** Whether the query should run. Set to false to prevent fetching until dependencies are ready. */
  enabled?: boolean;
}) {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const queryKey = useQueryKey(name);

  // Use single query approach for both paginated and non-paginated
  const { data, isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: queryFn,
    staleTime: FIVE_MINUTES,
    enabled
  });

  // Reset the legend toggle whenever a new dataset arrives so fresh series never start hidden
  const {
    visibility: legendVisibility,
    toggle: toggleLegend,
    setHovered: setHoveredLegend
  } = useLegendVisibilityState(data);

  useEffect(() => {
    if (loadingCallback) {
      loadingCallback(isLoading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div style={{ backgroundColor: 'white' }} ref={chartContainerRef}>
      <BeaconProChartContext.Provider
        value={{
          chartContainerRef,
          chartName: name,
          isLoading: isLoading || forceLoading,
          data,
          legendVisibility,
          toggleLegend,
          setHoveredLegend
        }}
      >
        <Flex flexDirection="column">{children}</Flex>
      </BeaconProChartContext.Provider>
    </div>
  );
}

export interface OptionProps {
  id: string;
  label: string;
  queryFn: () => Promise<unknown>;
}

/**
 * Default button that opens a context menu to export chart visualization/data.
 * Automatically includes "Ask Advisor" when rendered inside a ChartContextProvider.
 */
export function DefaultMenuButton({ excludeActions = [] }: { excludeActions?: string[] }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data, chartName, chartContainerRef } = useContext(BeaconProChartContext);
  const theme = useStacklineTheme();

  const { saveImage, copyImage, exportCsv } = useCsvExporter(chartName, chartContainerRef);

  const contextGroup = useChartContextGroup();
  const chartTitleFromCtx = useChartContextTitle();
  const contextDescription = contextGroup ? getChartContextGroup(contextGroup)?.description : undefined;
  const { handleAskAdvisor, shouldShow: shouldShowAdvisor } = useAskAdvisorAction({
    contextGroup,
    description: contextDescription
  });

  const dropdownOptions = useMemo(() => {
    const options: { id: string; label: string; endAdornment?: React.ReactNode }[] = [
      { id: 'saveImage', label: 'Save image' },
      { id: 'copyImage', label: 'Copy image' },
      { id: 'exportCsv', label: 'Export CSV' }
    ];

    if (shouldShowAdvisor) {
      options.push({
        id: 'askAdvisor',
        label: 'Ask Advisor',
        endAdornment: <AskAdvisorAIAnimation size={24} />
      });
    }

    return options.filter((opt) => !excludeActions.includes(opt.id));
  }, [shouldShowAdvisor, excludeActions]);

  const handleOptionClick = (optionId: string) => {
    setAnchorEl(null);

    switch (optionId) {
      case 'saveImage':
        saveImage();
        break;
      case 'copyImage':
        copyImage();
        break;
      case 'exportCsv':
        exportCsv(data);
        break;
      case 'askAdvisor': {
        const element = chartContainerRef?.current;
        const screenshotPromise = element ? captureElementAsWebp(element) : undefined;
        const chartHeight = element?.offsetHeight;
        const visibleTitle =
          element?.innerText?.split('\n')[0]?.replace(/\s+/g, ' ')?.trim() || chartTitleFromCtx || chartName;
        handleAskAdvisor(screenshotPromise, visibleTitle, undefined, chartHeight);
        break;
      }
      default:
        console.warn('No handler for option', optionId);
    }
  };

  return (
    <div>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-screenshot-exclude
        sx={{
          height: theme.spacing.mdl,
          width: theme.spacing.mdl,
          borderRadius: theme.spacing.xs,
          color: theme.colors.primary
        }}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '4px',
              boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.1)'
            }
          }
        }}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        {dropdownOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleOptionClick(option.id)}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'Roboto, sans-serif',
              color: '#031425',
              letterSpacing: '0.08px',
              gap: '4px',
              padding: '8px 12px'
            }}
          >
            {option.label}
            {option.endAdornment}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

BeaconProChart.Header = function BeaconProChartHeader({
  children,
  menuButton = <DefaultMenuButton excludeActions={[]} />,
  sx
}: {
  children: React.ReactNode;
  menuButton?: React.ReactNode | ((data: any, isLoading: boolean) => React.ReactNode);
  sx?: SxProps<Theme>;
}) {
  const { data, isLoading } = useContext(BeaconProChartContext);
  return (
    <Flex justifyContent="space-between" alignItems="center" sx={sx}>
      {children}
      {typeof menuButton === 'function' ? menuButton(data, isLoading) : menuButton}
    </Flex>
  );
};

/**
 * Title component of the chart, which can be a simple title or a dropdown title.
 *
 * @param title: title of the chart
 * @param prefix: prefix used in the case of dropdown
 * @param menuButton: overrides the default export menu button
 *
 * @returns a simple title or a dropdown title depending on the number of options registered
 */
BeaconProChart.Title = function BeaconProChartTitle({ children }: { children: React.ReactNode }) {
  return (
    <Flex alignItems="center" gap={6}>
      {children}
    </Flex>
  );
};

BeaconProChart.Subtitle = function BeaconProChartSubtitle({
  children,
  render,
  renderLoading = () => <SlSkeleton variant="rounded" height={20} width="100%" sx={{ marginTop: '10px' }} />
}: {
  children?: React.ReactNode;
  render?: (data: any, isLoading: boolean) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
}) {
  const { data, isLoading } = React.useContext(BeaconProChartContext);

  if (isLoading) {
    return renderLoading();
  }

  const subtitle = render ? render(data, isLoading) : children;
  return (
    <Text variant="body1" sx={{ marginTop: '8px' }}>
      {subtitle}
    </Text>
  );
};

interface LegendProps {
  primaryLegendProps?: Omit<LegendWithMetricProps, 'color'>;
  comparisonLegendProps?: Omit<LegendWithMetricProps, 'color'>;
  render?: (data: any) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
}

/**
 * Legend component of the chart, which can be rendered in a custom way or using the default LegendWithMetric component.
 *
 * @param render (optional): custom render call backfunction for the legend. The data will be passed as an argument. This allows the caller to render the legend in a custom way based off of the chart data.
 * @param primaryLegendProps or comparisonLegendProps: if render is not supplised, these props will be used to render a LegendWithMetric component
 *
 * @returns custom render or default LegendWithMetric component
 */
BeaconProChart.Legend = function BeaconProChartLegend({
  primaryLegendProps,
  comparisonLegendProps,
  render,
  renderLoading = () => {
    return (
      <Flex gap={32} marginY="32px">
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rounded" height={28} width={68} />
          <SlSkeleton variant="rounded" height={20} width={180} />
        </Flex>
        <Flex flexDirection="column" gap={8}>
          <SlSkeleton variant="rounded" height={28} width={68} />
          <SlSkeleton variant="rounded" height={20} width={180} />
        </Flex>
      </Flex>
    );
  }
}: LegendProps) {
  // TODO(tyan): add rendering/skeleton logic for isLoading
  const { data, isLoading, legendVisibility, toggleLegend, setHoveredLegend } = React.useContext(BeaconProChartContext);

  if (isLoading) {
    return renderLoading();
  }

  if (render && data) {
    return render(data);
  }

  // Both legends present: clicking one toggles its series in the visualization (never both off)
  const isToggleable = !!primaryLegendProps && !!comparisonLegendProps;

  return (
    <Flex gap="lg" sx={{ marginTop: '32px' }}>
      {comparisonLegendProps &&
        (isToggleable ? (
          <ToggleableLegend
            visible={legendVisibility.comparisonVisible}
            onToggle={() => toggleLegend('comparison')}
            onHoverChange={(hovering) => setHoveredLegend(hovering ? 'comparison' : null)}
          >
            <LegendWithMetric color="casper" {...comparisonLegendProps} />
          </ToggleableLegend>
        ) : (
          <LegendWithMetric color="casper" {...comparisonLegendProps} />
        ))}
      {primaryLegendProps &&
        (isToggleable ? (
          <ToggleableLegend
            visible={legendVisibility.primaryVisible}
            onToggle={() => toggleLegend('primary')}
            onHoverChange={(hovering) => setHoveredLegend(hovering ? 'primary' : null)}
          >
            <LegendWithMetric color="primary" {...primaryLegendProps} />
          </ToggleableLegend>
        ) : (
          <LegendWithMetric color="primary" {...primaryLegendProps} />
        ))}
    </Flex>
  );
};

interface VisualizationProps {
  render: (data: unknown, isLoading: boolean) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  deferLoading?: boolean; // If true, passes isLoading to render function instead of showing loading state
}

/**
 * The main visualization component of the chart, which will render the chart based on the data fetched.
 *
 * @param render: custom render call backfunction for the legend. The data will be passed as an argument. This allows the caller to render the chart in a custom way based off of the chart data.
 * @param deferLoading: if true, passes isLoading to render function instead of showing loading state
 *
 * @returns custom rendered chart defined by the caller
 */
BeaconProChart.Visualization = function BeaconProChartVisualization({
  render,
  renderLoading = () => <SlSkeleton variant="rounded" height={280} width="100%" />,
  deferLoading = false
}: VisualizationProps) {
  const { data, isLoading, legendVisibility } = React.useContext(BeaconProChartContext);

  if (isLoading && !deferLoading) {
    return renderLoading();
  }

  return (
    <ChartLegendVisibilityContext.Provider value={legendVisibility}>
      {render(data, isLoading)}
    </ChartLegendVisibilityContext.Provider>
  );
};
